import React, { useState } from 'react';
import { type FilterOperator, type ColumnFilter } from '../utils/tableFilterUtils';

interface ColumnFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  field: string;
  existingFilters: ColumnFilter[];
  onApply: (filters: ColumnFilter[]) => void;
}

const OPERATORS: { value: FilterOperator; label: string }[] = [
  { value: '=', label: '= (equals)' },
  { value: '!=', label: '!= (not equals)' },
  { value: '>', label: '> (greater than)' },
  { value: '<', label: '< (less than)' },
  { value: '>=', label: '>= (greater or equal)' },
  { value: '<=', label: '<= (less or equal)' },
  { value: 'IN', label: 'IN (comma-separated list)' },
];

export const ColumnFilterModal: React.FC<ColumnFilterModalProps> = ({
  isOpen,
  onClose,
  field,
  existingFilters,
  onApply,
}) => {
  const [filters, setFilters] = useState<ColumnFilter[]>(
    existingFilters.length > 0
      ? existingFilters.map((f) => ({ ...f }))
      : [{ field, operator: '=', value: '' }]
  );

  if (!isOpen) return null;

  const handleAddFilter = () => {
    setFilters([...filters, { field, operator: '=', value: '' }]);
  };

  const handleRemoveFilter = (index: number) => {
    const updated = filters.filter((_, i) => i !== index);
    if (updated.length === 0) {
      setFilters([{ field, operator: '=', value: '' }]);
    } else {
      setFilters(updated);
    }
  };

  const handleOperatorChange = (index: number, operator: FilterOperator) => {
    const updated = [...filters];
    updated[index] = { ...updated[index], operator };
    setFilters(updated);
  };

  const handleValueChange = (index: number, value: string) => {
    const updated = [...filters];
    updated[index] = { ...updated[index], value };
    setFilters(updated);
  };

  const handleApply = () => {
    // Only apply filters that have a value
    const validFilters = filters.filter((f) => f.value.trim() !== '');
    onApply(validFilters);
    onClose();
  };

  const handleClear = () => {
    onApply([]);
    onClose();
  };

  return (
    <div className="column-filter-overlay" onClick={onClose}>
      <div className="column-filter-modal" onClick={(e) => e.stopPropagation()}>
        <div className="column-filter-header">
          <h3>Filter: {field}</h3>
          <button className="close-btn" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="column-filter-body">
          <p className="filter-hint">
            All filters on this column are combined with AND logic.
            {' '}String comparisons are case-insensitive.
            {' '}IN uses comma-separated values.
          </p>

          {filters.map((filter, index) => (
            <div key={index} className="filter-row">
              <select
                className="filter-operator-select"
                value={filter.operator}
                onChange={(e) => handleOperatorChange(index, e.target.value as FilterOperator)}
              >
                {OPERATORS.map((op) => (
                  <option key={op.value} value={op.value}>
                    {op.label}
                  </option>
                ))}
              </select>
              <input
                type="text"
                className="filter-value-input"
                value={filter.value}
                onChange={(e) => handleValueChange(index, e.target.value)}
                placeholder={filter.operator === 'IN' ? 'val1, val2, val3' : 'Enter value...'}
                autoFocus={index === filters.length - 1}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleApply();
                }}
              />
              <button
                className="filter-remove-btn"
                onClick={() => handleRemoveFilter(index)}
                title="Remove filter"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}

          <button className="filter-add-btn" onClick={handleAddFilter}>
            + Add condition
          </button>
        </div>

        <div className="column-filter-footer">
          <button className="filter-clear-btn" onClick={handleClear}>
            Clear All
          </button>
          <button className="filter-apply-btn" onClick={handleApply}>
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};
